import {
  similarTitle,
  toMinutes,
  WEEKDAY_SHORT,
  type Assignees,
  type MagicEdit,
  type MagicTask,
} from '@shh/shared';
import {
  deleteEvent,
  deleteNote,
  deleteTask,
  deleteTodo,
  saveEvent,
  saveNote,
  saveTask,
  saveTodo,
} from '../data/api';
import { dayLabel } from '../data/agenda';
import { occursOn } from '../data/derive';
import type { useFamily } from '../data/family';
import { locale } from '../lib/locale';
import { magicText } from '../i18n/magic';

/**
 * "Einfach sagen 2": tasks with stars, and changing or deleting what exists. The AI only said
 * *what* is meant ("Zahnarzt am Dienstag") – the matching entries are found here, on the device,
 * so the family's calendar never goes to the AI.
 */
type FamilyData = ReturnType<typeof useFamily>;

export interface Candidate {
  id: string;
  label: string;
}

const DAYPART: Record<MagicTask['daypart'], string> = magicText.dayparts;

export function describeTask(t: MagicTask): string {
  const when = t.date
    ? magicText.once
    : t.days?.length
      ? t.days.map((d) => WEEKDAY_SHORT[locale][d - 1]).join(' ')
      : magicText.everyDay;
  return [
    t.who.length ? t.who.join(', ') : magicText.forAll,
    magicText.stars(t.stars),
    DAYPART[t.daypart],
    when,
  ].join(' · ');
}

/** The existing entries the family may mean – best guesses first. */
export function findCandidates(f: FamilyData, edit: MagicEdit): Candidate[] {
  switch (edit.what) {
    case 'event': {
      const day = edit.targetDate;
      return f.events
        .filter((e) => similarTitle(edit.target, e.title))
        .filter((e) => (day ? occursOn(e, day) : e.repeat || (e.endDate ?? e.date) >= f.today))
        .sort((a, b) => (a.date + (a.start ?? '')).localeCompare(b.date + (b.start ?? '')))
        .slice(0, 4)
        .map((e) => ({
          id: e.id,
          label: [
            e.title,
            e.repeat ? magicText.wholeSeries : dayLabel(f, e.date, true).label,
            e.start ? magicText.clock(e.start) : magicText.allDay,
          ].join(' · '),
        }));
    }
    case 'todo':
      return f.todos
        .filter((t) => !t.done && similarTitle(edit.target, t.title))
        .map((t) => ({ id: t.id, label: t.title }));
    case 'task':
      return f.tasks
        .filter((t) => t.active !== false && similarTitle(edit.target, t.title))
        .map((t) => ({ id: t.id, label: `${t.title} · ${magicText.stars(t.points)}` }));
    case 'note':
      return f.notes
        .filter((n) => similarTitle(edit.target, n.text))
        .map((n) => ({ id: n.id, label: n.text }));
  }
}

/** "17:00 → 18:00", "Di → Mi" – what a change will do, in a few words. */
export function describeChange(f: FamilyData, edit: MagicEdit): string {
  const s = edit.set;
  return [
    s.title && magicText.newName(s.title),
    s.date && magicText.onDay(dayLabel(f, s.date, true).label),
    s.start && magicText.clock(`${s.start}${s.end ? `–${s.end}` : ''}`),
    s.who && magicText.forWho(s.who.join(', ')),
  ]
    .filter(Boolean)
    .join(' · ');
}

const shift = (time: string, minutes: number) => {
  const t = (toMinutes(time) + minutes + 24 * 60) % (24 * 60);
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};

export async function applyEdit(
  f: FamilyData,
  edit: MagicEdit,
  id: string,
  assignees: (names: string[]) => Assignees,
) {
  const hid = f.householdId;
  if (edit.action === 'delete') {
    if (edit.what === 'event') return deleteEvent(hid, id);
    if (edit.what === 'todo') return deleteTodo(hid, id);
    if (edit.what === 'task') return deleteTask(hid, id);
    return deleteNote(hid, id);
  }
  const s = edit.set;
  if (edit.what === 'event') {
    const e = f.events.find((x) => x.id === id);
    if (!e) throw new Error('Termin nicht mehr da');
    // a new start keeps the length of the event ("Fußball ist erst um 18 Uhr")
    const end =
      s.end ??
      (s.start && e.start && e.end
        ? shift(s.end ?? e.end, toMinutes(s.start) - toMinutes(e.start))
        : e.end);
    return saveEvent(hid, {
      id,
      icon: e.icon,
      endDate: e.endDate,
      repeat: e.repeat,
      recurring: e.recurring,
      location: e.location,
      transport: e.transport,
      bring: e.bring,
      notes: e.notes,
      createdBy: e.createdBy,
      title: s.title ?? e.title,
      // a series keeps its first day; only time and name change
      date: s.date && !e.repeat ? s.date : e.date,
      start: s.start ?? e.start,
      end,
      assignees: s.who ? assignees(s.who) : e.assignees,
    });
  }
  if (edit.what === 'todo') {
    const t = f.todos.find((x) => x.id === id);
    if (!t) throw new Error('To-do nicht mehr da');
    return saveTodo(hid, {
      id,
      title: s.title ?? t.title,
      icon: t.icon,
      assignees: s.who ? assignees(s.who) : t.assignees,
      due: s.date ?? t.due,
      createdBy: t.createdBy,
    });
  }
  if (edit.what === 'task') {
    const t = f.tasks.find((x) => x.id === id);
    if (!t) throw new Error('Aufgabe nicht mehr da');
    return saveTask(hid, {
      id,
      title: s.title ?? t.title,
      icon: t.icon,
      points: t.points,
      daypart: t.daypart,
      repeat: t.repeat,
      requiresApproval: t.requiresApproval,
      assignees: s.who ? assignees(s.who) : t.assignees,
    });
  }
  const n = f.notes.find((x) => x.id === id);
  if (!n) throw new Error('Zettel nicht mehr da');
  return saveNote(hid, { id, text: s.title ?? n.text, until: s.date ?? n.until });
}

export function applyTask(f: FamilyData, t: MagicTask, assignees: (names: string[]) => Assignees) {
  return saveTask(f.householdId, {
    title: t.title,
    icon: '',
    points: t.stars,
    daypart: t.daypart,
    assignees: assignees(t.who),
    repeat: t.date
      ? { kind: 'once', date: t.date }
      : t.days?.length
        ? { kind: 'weekly', days: [...t.days].sort() }
        : { kind: 'daily' },
    requiresApproval: false,
    createdBy: f.meId,
  });
}
